import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { AlertBanner, Box, Button, Card, Group, Loader, Segmented, Select, Stack, Tag, Text, TextInput, Title } from '@grovus/design-system'
import { SETTINGS_COPY, type Lang, type SettingsCopy } from '../i18n'
import type { VerdictMetric } from '../creatives/contract'
import { fill, money } from '../creatives/format'
import { dayMonth } from '../share/date'
import { ActivityLog } from '../members/ActivityLog'
import { readSettings, saveSettings, SETTINGS_ERROR_FIELDS, type CompanySettings, type SettingsEdit, type SettingsField } from './api'

const LOCALE: Record<Lang, string> = { en: 'en-US', ru: 'ru-RU', uk: 'uk-UA' }
const BORDER = '1px solid var(--mantine-color-default-border)'

interface Props {
  lang: Lang
  /** The company the screen was mounted for. The shell keys the screen by it. */
  companyId: string
  /** A save answered `200`: the shell moves `reloadKey`, so the verdicts read again. */
  onSaved: (companyId: string) => void
  /** A `404`: the shell re-checks whether the caller still belongs. */
  onAccessLost: (companyId: string) => void
  /** Injected in tests; the real screen uses the global. */
  fetchImpl?: typeof fetch
}

/** The three edited fields as the form holds them; the target is the typed text. */
export interface Draft {
  primaryMetric: VerdictMetric
  target: string
  reportingCurrency: string | null
}

/** The GRO-186 fields as the form holds them: the typed text of each number. */
export interface StatDraft {
  direction: string
  provisional: string
  reliable: string
  multiple: string
  waitingDays: string
}

type Load = { phase: 'loading' } | { phase: 'ready'; settings: CompanySettings } | { phase: 'failed' }

/** The highest tier `PUT /api/settings` accepts. */
const TIER_MAX = 10_000

/** A target in the reader's locale, without grouping: two decimals, up to four for ROAS. */
export function formatTarget(value: number | null, metric: VerdictMetric, lang: Lang): string {
  if (value === null) return ''
  return new Intl.NumberFormat(LOCALE[lang], {
    minimumFractionDigits: 2,
    maximumFractionDigits: metric === 'cpa' ? 2 : 4,
    useGrouping: false,
  }).format(value)
}

/** The typed target: `null` when empty, `'invalid'` when it is not a plain number. A comma reads as the decimal point. */
export function parseTarget(text: string): number | null | 'invalid' {
  const cleaned = text.replace(/[\s  ]/g, '').replace(',', '.')
  if (cleaned === '') return null
  if (!/^\d+(\.\d+)?$/.test(cleaned)) return 'invalid'
  return Number(cleaned)
}

/**
 * The draft a screen starts from. A stale company (its stored currency held by no account)
 * starts from the derived currency with a CPA target cleared; a ROAS target is kept.
 */
export function initialDraft(settings: CompanySettings, lang: Lang): Draft {
  const stale = settings.reportingCurrencyStale
  const target = stale && settings.primaryMetric === 'cpa' ? null : settings.target
  return {
    primaryMetric: settings.primaryMetric,
    target: formatTarget(target, settings.primaryMetric, lang),
    reportingCurrency: stale ? settings.derivedReportingCurrency : settings.reportingCurrency,
  }
}

export function initialStat(settings: CompanySettings, lang: Lang): StatDraft {
  const plain = (value: number, digits: number) =>
    new Intl.NumberFormat(LOCALE[lang], { minimumFractionDigits: digits, maximumFractionDigits: digits, useGrouping: false }).format(value)
  return {
    direction: String(settings.tiers.direction),
    provisional: String(settings.tiers.provisional),
    reliable: String(settings.tiers.reliable),
    multiple: plain(settings.stopLossMultiple, 2),
    waitingDays: String(settings.waitingDays),
  }
}

/** A typed whole number, or `'invalid'`. */
export function parseWhole(text: string): number | 'invalid' {
  const cleaned = text.replace(/[\s  ]/g, '')
  return /^\d+$/.test(cleaned) ? Number(cleaned) : 'invalid'
}

/** The typed multiple rounded to two decimals, as the server stores it, or `'invalid'`. */
export function parseMultiple(text: string): number | 'invalid' {
  const parsed = parseTarget(text)
  return typeof parsed === 'number' ? Math.round(parsed * 100) / 100 : 'invalid'
}

/** The three tiers when they parse and keep 1 <= direction <= provisional <= reliable <= 10000, else `null`. */
export function tiersOf(stat: StatDraft): CompanySettings['tiers'] | null {
  const [direction, provisional, reliable] = [stat.direction, stat.provisional, stat.reliable].map(parseWhole)
  if (direction === 'invalid' || provisional === 'invalid' || reliable === 'invalid') return null
  if (direction < 1 || direction > provisional || provisional > reliable || reliable > TIER_MAX) return null
  return { direction, provisional, reliable }
}

/** The band the engine requires at `n` conversions, in percent (`verdict.rs`): max(10, 200 / sqrt(n)). */
export function bandAt(n: number): number {
  return Math.round(Math.max(10, 200 / Math.sqrt(n)))
}

/** The metric switch clears the target: the number means something else under the other metric. */
export function switchMetric(draft: Draft, metric: VerdictMetric): Draft {
  return metric === draft.primaryMetric ? draft : { ...draft, primaryMetric: metric, target: '' }
}

/** A currency change clears a CPA target, which is money in that currency; a ROAS target stays. */
export function switchCurrency(draft: Draft, currency: string | null): Draft {
  if (currency === draft.reportingCurrency) return draft
  return { ...draft, reportingCurrency: currency, target: draft.primaryMetric === 'cpa' ? '' : draft.target }
}

/**
 * A derived currency is served as `reportingCurrency`, so picking it compares equal; the
 * pick still counts while nothing is stored, because storing it stops it following spend.
 */
function changed(draft: Draft, settings: CompanySettings, currencyPicked: boolean): boolean {
  return draft.primaryMetric !== settings.primaryMetric
    || draft.reportingCurrency !== settings.reportingCurrency
    || (currencyPicked && !settings.reportingCurrencySet && draft.reportingCurrency !== null)
    || parseTarget(draft.target) !== settings.target
}

function statChanged(stat: StatDraft, settings: CompanySettings): boolean {
  return parseWhole(stat.direction) !== settings.tiers.direction
    || parseWhole(stat.provisional) !== settings.tiers.provisional
    || parseWhole(stat.reliable) !== settings.tiers.reliable
    || parseMultiple(stat.multiple) !== settings.stopLossMultiple
    || parseWhole(stat.waitingDays) !== settings.waitingDays
}

/**
 * Whether the server stored what was sent. A backend from before GRO-186 answers `200` with
 * the old values, because it builds its edit from the three GRO-185 fields alone.
 */
function stored(sent: SettingsEdit, body: CompanySettings): boolean {
  return body.tiers.direction === sent.tiers.direction
    && body.tiers.provisional === sent.tiers.provisional
    && body.tiers.reliable === sent.tiers.reliable
    && Math.round(body.stopLossMultiple * 100) === Math.round(sent.stopLossMultiple * 100)
    && body.waitingDays === sent.waitingDays
}

function errorText(code: string, copy: SettingsCopy): string {
  return {
    invalid_primary_metric: copy.invalidMetric,
    invalid_target: copy.invalidTarget,
    invalid_reporting_currency: copy.invalidCurrency,
    cpa_target_needs_currency: copy.cpaNeedsCurrency,
    invalid_tiers: copy.invalidTiers,
    invalid_stop_loss_multiple: copy.invalidMultiple,
    invalid_waiting_days: copy.invalidWaiting,
  }[code] ?? copy.failed
}

/** One setting: the label and its help on the left, the control on the right. */
function Row({ label, help, children, first }: { label: string; help?: string; children: ReactNode; first?: boolean }) {
  return (
    <Box
      style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, padding: '10px 0', borderTop: first ? undefined : BORDER }}
    >
      <Box>
        <Text fw={600}>{label}</Text>
        {help !== undefined && <Text size="xs" className="grv-muted" mt={4}>{help}</Text>}
      </Box>
      <Box>{children}</Box>
    </Box>
  )
}

function Fixed({ value, label }: { value: string; label?: string }) {
  return (
    <Box miw={120}>
      <Text fw={600}>{value}</Text>
      {label !== undefined && <Text size="xs" className="grv-muted">{label}</Text>}
    </Box>
  )
}

/**
 * The company settings: the primary metric, the target and the reporting currency
 * (GRO-185), and the confidence tiers, the stop-loss multiple and the waiting period
 * (GRO-186), which an Owner or an Admin edits. Any member who reaches the screen reads it;
 * without `canEdit` every control is disabled and the GRO-186 values read as text.
 */
export function SettingsScreen({ lang, companyId, onSaved, onAccessLost, fetchImpl }: Props) {
  const copy = SETTINGS_COPY[lang]
  const [load, setLoad] = useState<Load>({ phase: 'loading' })
  const [draft, setDraft] = useState<Draft | null>(null)
  const [stat, setStat] = useState<StatDraft | null>(null)
  const [currencyPicked, setCurrencyPicked] = useState(false)
  const [fieldError, setFieldError] = useState<{ field: SettingsField; code: string } | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [saving, setSaving] = useState(false)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let current = true
    setLoad({ phase: 'loading' })
    void readSettings(companyId, fetchImpl).then((outcome) => {
      if (!current) return
      if (outcome.status === 'ok') {
        setLoad({ phase: 'ready', settings: outcome.body })
        setDraft(initialDraft(outcome.body, lang))
        setStat(initialStat(outcome.body, lang))
        setCurrencyPicked(false)
        return
      }
      if (outcome.status === 'notFound') onAccessLost(companyId)
      if (outcome.status !== 'stale') setLoad({ phase: 'failed' })
    })
    return () => {
      current = false
    }
    // The draft is formatted once, in the language the screen opened in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, attempt, fetchImpl])

  const edit = useCallback((next: Draft) => {
    setDraft(next)
    setFieldError(null)
    setFailure(null)
    setSaved(false)
  }, [])
  const editStat = useCallback((next: StatDraft) => {
    setStat(next)
    setFieldError(null)
    setFailure(null)
    setSaved(false)
  }, [])

  if (load.phase === 'loading' || (load.phase === 'ready' && (draft === null || stat === null))) {
    return (
      <Group gap="sm" data-testid="settings-loading">
        <Loader size="sm" />
        <Text size="sm">{copy.loading}</Text>
      </Group>
    )
  }
  if (load.phase === 'failed' || draft === null || stat === null) {
    return (
      <Card p="lg" role="alert" data-testid="settings-failed">
        <Text>{copy.loadFailed}</Text>
        <Button mt="sm" onClick={() => setAttempt((n) => n + 1)}>{copy.retry}</Button>
      </Card>
    )
  }

  const { settings } = load
  const readOnly = !settings.canEdit
  const cpa = draft.primaryMetric === 'cpa'
  const noAccounts = settings.currencies.length === 0
  const locked = readOnly || saving
  const targetDisabled = locked || (cpa && draft.reportingCurrency === null)
  const parsed = parseTarget(draft.target)
  const targetSet = typeof parsed === 'number'
  const dirty = changed(draft, settings, currencyPicked) || statChanged(stat, settings)
  const errorOf = (field: SettingsField) => (fieldError?.field === field ? errorText(fieldError.code, copy) : undefined)
  const tiers = tiersOf(stat)
  const tiersError = tiers === null ? copy.invalidTiers : errorOf('tiers')
  const multiple = parseMultiple(stat.multiple)
  const shownMultiple = typeof multiple === 'number' ? multiple : settings.stopLossMultiple
  // One hint per tier the draft moves: the gap a verdict needs at that n.
  const hints = (['direction', 'provisional', 'reliable'] as const).flatMap((key) => {
    const n = parseWhole(stat[key])
    return typeof n === 'number' && n >= 1 && n !== settings.tiers[key] ? [{ key, n }] : []
  })
  const lowProvisional = tiers !== null && tiers.provisional <= 4

  const targetHelp = cpa && draft.reportingCurrency === null
    ? copy.targetHelpNoCur
    : !targetSet
      ? copy.targetHelpEmpty
      : cpa
        ? `${copy.targetHelpSet} ${fill(copy.targetHelpStopLoss, {
          m: shownMultiple,
          v: money(parsed * shownMultiple, draft.reportingCurrency, lang, 2),
        })}`
        : copy.targetHelpSet

  const save = async () => {
    if (parsed === 'invalid') {
      setFieldError({ field: 'target', code: 'invalid_target' })
      return
    }
    if (tiers === null) return
    if (multiple === 'invalid') {
      setFieldError({ field: 'multiple', code: 'invalid_stop_loss_multiple' })
      return
    }
    const waitingDays = parseWhole(stat.waitingDays)
    if (waitingDays === 'invalid') {
      setFieldError({ field: 'waiting', code: 'invalid_waiting_days' })
      return
    }
    const sent: SettingsEdit = {
      primaryMetric: draft.primaryMetric, target: parsed, reportingCurrency: draft.reportingCurrency,
      tiers, stopLossMultiple: multiple, waitingDays,
    }
    setSaving(true)
    setFailure(null)
    const outcome = await saveSettings(companyId, sent, fetchImpl)
    setSaving(false)
    switch (outcome.status) {
      case 'ok':
        // A server that answered with other values did not store the draft: the save is
        // shown as failed, the draft stays and nothing reads again.
        if (!stored(sent, outcome.body)) {
          setFailure(copy.notStored)
          return
        }
        setLoad({ phase: 'ready', settings: outcome.body })
        setDraft(initialDraft(outcome.body, lang))
        setStat(initialStat(outcome.body, lang))
        setCurrencyPicked(false)
        setFieldError(null)
        setSaved(true)
        onSaved(companyId)
        return
      case 'invalid': {
        const field = outcome.code === null ? undefined : SETTINGS_ERROR_FIELDS[outcome.code]
        if (field !== undefined && outcome.code !== null) setFieldError({ field, code: outcome.code })
        else setFailure(copy.failed)
        return
      }
      case 'forbidden':
        setFailure(copy.forbidden)
        return
      case 'notFound':
        onAccessLost(companyId)
        return
      case 'stale':
      case 'expired':
        return
      default:
        setFailure(copy.failed)
    }
  }

  return (
    <Stack gap="md" maw={880} data-testid="settings-screen">
      <Text className="grv-muted">{copy.sub}</Text>
      {readOnly && <AlertBanner tone="warn">{copy.readonly}</AlertBanner>}

      <Card p="lg">
        <Title order={2} fz="md" mb="sm">{copy.secRef}</Title>
        <Row label={copy.metric} help={copy.metricHelp} first>
          <Segmented
            aria-label={copy.metric}
            value={draft.primaryMetric}
            onChange={(value) => edit(switchMetric(draft, value as VerdictMetric))}
            data={[{ value: 'roas', label: 'ROAS' }, { value: 'cpa', label: 'CPA' }]}
            disabled={locked}
            data-testid="settings-metric"
          />
          {errorOf('metric') !== undefined && <Text size="sm" c="bad.7" role="alert" mt={4}>{errorOf('metric')}</Text>}
        </Row>
        <Row label={cpa ? copy.targetCpa : copy.targetRoas}>
          <TextInput
            aria-label={cpa ? copy.targetCpa : copy.targetRoas}
            value={draft.target}
            onChange={(event) => edit({ ...draft, target: event.currentTarget.value })}
            placeholder={cpa ? undefined : copy.targetPlaceholder}
            disabled={targetDisabled}
            rightSection={cpa && draft.reportingCurrency !== null ? <Text size="xs" className="grv-muted">{draft.reportingCurrency}</Text> : undefined}
            rightSectionWidth={48}
            error={errorOf('target')}
            w={{ base: '100%', sm: 180 }}
            data-testid="settings-target"
          />
          <Text size="xs" className="grv-muted" mt={4} data-testid="settings-target-help">{targetHelp}</Text>
        </Row>
        <Row label={copy.currency}>
          {settings.reportingCurrencyStale && settings.reportingCurrency !== null && (
            <Text size="sm" className="grv-warn" mb={6} data-testid="settings-stale">
              {fill(copy.staleNote, { c: settings.reportingCurrency })}
            </Text>
          )}
          <Group gap="xs" wrap="nowrap">
            <Select
              aria-label={copy.currency}
              value={draft.reportingCurrency}
              onChange={(value) => edit(switchCurrency(draft, value))}
              onOptionSubmit={() => setCurrencyPicked(true)}
              data={settings.currencies}
              placeholder="-"
              allowDeselect={false}
              disabled={locked || noAccounts}
              error={errorOf('currency')}
              w={{ base: '100%', sm: 180 }}
              data-testid="settings-currency"
            />
            {!settings.reportingCurrencySet && !noAccounts && draft.reportingCurrency === settings.derivedReportingCurrency && (
              <Tag>{copy.currencyDerived}</Tag>
            )}
          </Group>
          <Text size="xs" className="grv-muted" mt={4}>
            {noAccounts ? copy.noAccounts : cpa && targetSet ? `${copy.currencyHelp} ${copy.currencyClears}` : copy.currencyHelp}
          </Text>
        </Row>
      </Card>

      <Card p="lg">
        <Title order={2} fz="md" mb="sm">{copy.secStat}</Title>
        <Row label={copy.tiers} help={copy.tiersHelp} first>
          {readOnly ? (
            <Group gap="lg">
              <Fixed value={`n = ${settings.tiers.direction}`} label={copy.tierDirection} />
              <Fixed value={`n = ${settings.tiers.provisional}`} label={copy.tierProvisional} />
              <Fixed value={`n = ${settings.tiers.reliable}`} label={copy.tierReliable} />
            </Group>
          ) : (
            <>
              <Group gap="sm" wrap="nowrap" align="flex-start">
                {(['direction', 'provisional', 'reliable'] as const).map((key) => {
                  const label = { direction: copy.tierDirection, provisional: copy.tierProvisional, reliable: copy.tierReliable }[key]
                  return (
                    <TextInput
                      key={key}
                      label={label}
                      aria-label={label}
                      value={stat[key]}
                      onChange={(event) => editStat({ ...stat, [key]: event.currentTarget.value })}
                      disabled={locked}
                      error={tiersError !== undefined}
                      inputMode="numeric"
                      w={88}
                      data-testid={`settings-tier-${key}`}
                    />
                  )
                })}
              </Group>
              {tiersError !== undefined && <Text size="sm" c="bad.7" role="alert" mt={4} data-testid="settings-tiers-error">{tiersError}</Text>}
              {hints.map(({ key, n }) => (
                <Text key={key} size="xs" className="grv-muted" mt={4} data-testid="settings-tier-hint">
                  {fill(copy.tierHint, { n, g: bandAt(n) })}
                </Text>
              ))}
              {lowProvisional && <Text size="xs" className="grv-warn" mt={4} data-testid="settings-tier-low">{copy.tierLowNote}</Text>}
            </>
          )}
        </Row>
        <Row label={copy.stopLoss} help={copy.stopLossHelp}>
          {readOnly ? (
            <Fixed value={fill(copy.stopLossValue, { m: settings.stopLossMultiple })} />
          ) : (
            <TextInput
              aria-label={copy.stopLoss}
              value={stat.multiple}
              onChange={(event) => editStat({ ...stat, multiple: event.currentTarget.value })}
              disabled={locked}
              rightSection={<Text size="xs" className="grv-muted">×</Text>}
              error={errorOf('multiple')}
              inputMode="decimal"
              w={{ base: '100%', sm: 180 }}
              data-testid="settings-multiple"
            />
          )}
        </Row>
        <Row label={copy.wait} help={copy.waitHelp}>
          {readOnly ? (
            <Fixed value={fill(copy.waitValue, { n: settings.waitingDays })} />
          ) : (
            <TextInput
              aria-label={copy.wait}
              value={stat.waitingDays}
              onChange={(event) => editStat({ ...stat, waitingDays: event.currentTarget.value })}
              disabled={locked}
              rightSection={<Text size="xs" className="grv-muted">{copy.days}</Text>}
              rightSectionWidth={48}
              error={errorOf('waiting')}
              inputMode="numeric"
              w={{ base: '100%', sm: 180 }}
              data-testid="settings-waiting"
            />
          )}
        </Row>
      </Card>

      {!readOnly && (
        <Group gap="md">
          <Button onClick={() => { void save() }} disabled={!dirty || saving || tiers === null} loading={saving} data-testid="settings-save">
            {copy.save}
          </Button>
          <Button
            variant="default"
            onClick={() => {
              edit(initialDraft(settings, lang))
              setStat(initialStat(settings, lang))
              setCurrencyPicked(false)
            }}
            disabled={!dirty || saving}
          >
            {copy.cancel}
          </Button>
          <Text size="sm" className="grv-muted" role="status" data-testid="settings-status">
            {failure ?? (saved ? copy.saved : settings.updatedAt !== null ? fill(copy.lastChanged, { d: dayMonth(settings.updatedAt, lang) }) : '')}
          </Text>
        </Group>
      )}

      <ActivityLog companyId={companyId} lang={lang} scope="company" onAccessLost={onAccessLost} fetchImpl={fetchImpl} />
    </Stack>
  )
}
